import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import {
  api, ApiFailure, ageLabel, genderShort,
  APPOINTMENT_STATUS_LABEL,
  type Appointment, type DaySchedule, type Doctor, type PatientMatch, type Slot,
} from '../api'
import { CategoryPill } from '../components/PatientCategory'

function today(): string {
  return new Date().toLocaleDateString('en-CA')
}

function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1)
  dt.setDate(dt.getDate() + days)
  return dt.toLocaleDateString('en-CA')
}

function prettyDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(y!, (m ?? 1) - 1, d ?? 1)
    .toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

function hm(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** Appointments still live on the queue; the terminal ones drop off the list. */
const LIVE = new Set(['booked', 'confirmed', 'arrived', 'in_consult'])

type Dialog =
  /** `past` when the slot has already started - booking it needs an override. */
  | { kind: 'book'; slotIso: string; time: string; past: boolean }
  | { kind: 'move'; slotIso: string; time: string; past: boolean; appt: Appointment }
  | { kind: 'cancel'; appt: Appointment }
  | null

export default function Appointments() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [doctorId, setDoctorId] = useState('')
  const [date, setDate] = useState(today())
  const [schedule, setSchedule] = useState<DaySchedule | null>(null)
  const [appts, setAppts] = useState<Appointment[]>([])
  const [dialog, setDialog] = useState<Dialog>(null)
  const [moving, setMoving] = useState<Appointment | null>(null)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void api.get<{ doctors: Doctor[] }>('/api/doctors').then((r) => setDoctors(r.doctors))
  }, [])

  useEffect(() => {
    if (user?.doctorId) setDoctorId(user.doctorId)
    else if (doctors.length > 0) setDoctorId((d) => d || doctors[0]!.id)
  }, [user, doctors])

  const load = useCallback(async () => {
    if (!doctorId) return
    const [s, a] = await Promise.all([
      api.get<{ schedule: DaySchedule }>(`/api/schedule/day?doctorId=${doctorId}&date=${date}`),
      api.get<{ appointments: Appointment[] }>(`/api/appointments?doctorId=${doctorId}&date=${date}`),
    ])
    setSchedule(s.schedule)
    setAppts(a.appointments)
  }, [doctorId, date])

  useEffect(() => { void load().catch(() => setMsg({ kind: 'error', text: 'Could not load the day.' })) }, [load])

  async function run(fn: () => Promise<void>, ok?: string) {
    setBusy(true); setMsg(null)
    try {
      await fn()
      await load()
      if (ok) setMsg({ kind: 'ok', text: ok })
    } catch (err) {
      setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : 'That did not work.' })
    } finally {
      setBusy(false)
    }
  }

  function onSlot(slot: Slot) {
    // A taken or held slot is refused by the server, so it is not offered.
    if (slot.booked || slot.held) return
    const past = Boolean(slot.past)
    if (moving) {
      setDialog({ kind: 'move', slotIso: slot.iso, time: slot.time, past, appt: moving })
      setMoving(null)
      return
    }
    setDialog({ kind: 'book', slotIso: slot.iso, time: slot.time, past })
  }

  const step = { confirm: 'confirm', arrive: 'arrive', 'no-show': 'no-show' } as const
  const stepAction = (appt: Appointment, action: keyof typeof step, ok: string) =>
    run(() => api.post(`/api/appointments/${appt.id}/${action}`), ok)

  async function checkIn(appt: Appointment) {
    await run(async () => {
      const r = await api.post<{ visit: { id: string } }>('/api/visits', {
        patientId: appt.patientId, doctorId: appt.doctorId, appointmentId: appt.id,
      })
      navigate(`/visits/${r.visit.id}`)
    })
  }

  const doctorName = doctors.find((d) => d.id === doctorId)?.fullName ?? ''

  return (
    <>
      <div className="page-head no-print">
        <h2>Appointments</h2>
        <p>Walk-ins and booked patients share one queue. A slot holds one patient; booking a past time, a full session or a closed day needs an override with a reason.</p>
      </div>

      {msg && <div className={`msg ${msg.kind}`}>{msg.text}</div>}

      {moving && (
        <div className="msg warn moving-banner">
          Rescheduling <strong>{moving.patientName}</strong> — pick a free slot, or change the day.
          <button className="link" onClick={() => { setMoving(null); setMsg(null) }}>Cancel</button>
        </div>
      )}

      <div className="appt-bar">
        {!user?.doctorId && (
          <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
            {doctors.map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}
          </select>
        )}
        <div className="appt-daynav">
          <button className="secondary" onClick={() => setDate(shiftDate(date, -1))}>←</button>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value || today())} />
          <button className="secondary" onClick={() => setDate(shiftDate(date, 1))}>→</button>
          {date !== today() && <button className="link" onClick={() => setDate(today())}>Today</button>}
        </div>
      </div>

      <div className="console">
        <div>
          <div className="panel">
            <h3>{prettyDate(date)} — {doctorName}</h3>
            {schedule?.closed && (
              <div className="msg warn" style={{ marginTop: 8 }}>
                Closed{schedule.reason ? ` — ${schedule.reason}` : ''}. No slots can be booked on this day.
              </div>
            )}
            {schedule && !schedule.closed && schedule.sessions.length === 0 && (
              <p className="panel-note">No sessions set for this day.</p>
            )}
            {schedule?.sessions.map((s) => (
              <div key={`${s.label}-${s.startTime}`} className="appt-session">
                <div className="appt-session-head">
                  <strong>{s.label}</strong>
                  <span className="panel-note" style={{ margin: 0 }}>
                    {s.startTime.slice(0, 5)}–{s.endTime.slice(0, 5)}
                    {s.maxPatients != null && (
                      <span className={s.overCap ? 'over' : ''}> · {s.bookedCount}/{s.maxPatients} booked</span>
                    )}
                  </span>
                </div>
                <div className="slot-grid">
                  {/* One patient per slot: the server refuses a second booking
                      on the same instant (appt_doctor_slot_uq), so a taken or
                      held slot is shown but not offered. A past slot stays
                      clickable - a walk-in recorded late - and the dialog
                      asks for an override reason. */}
                  {s.slots.map((slot, i) => (
                    <button
                      key={`${slot.time}-${i}`}
                      type="button"
                      disabled={busy || slot.booked || slot.held}
                      className={`slot${slot.booked || slot.held ? ' taken' : ''}${slot.past && !slot.booked ? ' past' : ''}${slot.overflow ? ' overflow' : ''}`}
                      /* The time is in the accessible name, not only the tooltip:
                         forty-two buttons all reading "Free — click to book" tell
                         a screen-reader user nothing about which one they are on. */
                      aria-label={slot.booked
                        ? `${slot.time} — booked for ${slot.patientName}.`
                        : slot.held
                          ? `${slot.time} — held while a patient pays.`
                          : slot.past
                            ? `${slot.time} — already past. Click to book with an override.`
                            : `${slot.time} — free. Click to book.`}
                      title={slot.booked
                        ? `${slot.patientName}`
                        : slot.held ? 'Held while a patient pays online' : slot.past ? 'Past — needs an override' : 'Free — click to book'}
                      onClick={() => onSlot(slot)}
                    >
                      <span className="slot-time">{slot.time}</span>
                      {slot.booked && <span className="slot-who">{slot.patientName}</span>}
                      {slot.held && <span className="slot-who">Paying…</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {schedule && !schedule.closed && (() => {
              const inSessions = schedule.sessions.reduce((n, s) => n + s.bookedCount, 0)
              const total = appts.filter((a) => LIVE.has(a.status)).length
              return (
                <p className="panel-note">
                  Day total: {total} booked{total > inSessions ? ` (${total - inSessions} outside the sessions above)` : ''}.
                </p>
              )
            })()}
          </div>
        </div>

        <aside>
          <div className="panel">
            <h3>Booked · {appts.filter((a) => LIVE.has(a.status)).length}</h3>
            {appts.length === 0 && <p className="panel-note">Nothing booked yet.</p>}
            <div className="appt-list">
              {appts.map((a) => (
                <div key={a.id} className={`appt-card status-${a.status}`}>
                  <div className="appt-card-head">
                    <span className="appt-time">{hm(a.scheduledAt)}</span>
                    <span className={`pill ${a.status === 'completed' ? 'on' : LIVE.has(a.status) ? '' : 'off'}`}>
                      {APPOINTMENT_STATUS_LABEL[a.status]}
                    </span>
                  </div>
                  <div className="appt-who">{a.patientName}
                    <span className="panel-note"> {ageLabel(a)}/{genderShort(a.gender)}</span>
                    {' '}<CategoryPill category={a.category} />
                  </div>
                  {a.allergies && <div className="queue-allergy">Allergy: {a.allergies}</div>}
                  {a.rescheduledFromId && <div className="panel-note" style={{ margin: 0 }}>Rescheduled</div>}
                  {LIVE.has(a.status) && (
                    <div className="appt-actions">
                      {a.status === 'booked' && (
                        <button className="link" disabled={busy} onClick={() => void stepAction(a, 'confirm', 'Confirmed.')}>Confirm</button>
                      )}
                      {a.status !== 'in_consult' && a.status !== 'arrived' && (
                        <button className="link" disabled={busy} onClick={() => void checkIn(a)}>Check in</button>
                      )}
                      <button className="link" disabled={busy} onClick={() => { setMoving(a); setMsg({ kind: 'warn', text: `Pick a new slot for ${a.patientName}.` }) }}>Reschedule</button>
                      <button className="link" disabled={busy} onClick={() => setDialog({ kind: 'cancel', appt: a })}>Cancel</button>
                      {a.status === 'booked' && (
                        <button className="link" disabled={busy} onClick={() => void stepAction(a, 'no-show', 'Marked no-show.')}>No-show</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {dialog && (
        <ApptDialog
          dialog={dialog}
          doctorId={doctorId}
          doctorName={doctorName}
          date={date}
          onClose={() => setDialog(null)}
          onDone={(text) => {
            setDialog(null)
            void load()
            // An override is worth flagging; an ordinary booking is just done.
            if (text) setMsg({ kind: text.startsWith('Booked with override') ? 'warn' : 'ok', text })
          }}
          onError={(text) => setMsg({ kind: 'error', text })}
        />
      )}
    </>
  )
}

/** Book, move, or cancel — the three actions that need a bit more than a click. */
function ApptDialog({
  dialog, doctorId, doctorName, date, onClose, onDone, onError,
}: {
  dialog: NonNullable<Dialog>
  doctorId: string
  doctorName: string
  date: string
  onClose: () => void
  onDone: (warning?: string) => void
  onError: (text: string) => void
}) {
  const [q, setQ] = useState('')
  const [matches, setMatches] = useState<PatientMatch[]>([])
  const [picked, setPicked] = useState<PatientMatch | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [quote, setQuote] = useState<string | null>(null)
  /** The server's refusal, shown here so nothing typed is lost. */
  const [error, setError] = useState<string | null>(null)
  /** The server said every problem is one reception may book through. */
  const [overridable, setOverridable] = useState(dialog.kind !== 'cancel' && dialog.past)
  const [override, setOverride] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')
  /** One key per dialog: a double click or a retry cannot book twice. */
  const attemptKey = useRef(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  )

  useEffect(() => {
    if (dialog.kind !== 'book') return
    const term = q.trim()
    if (term.length < 2) { setMatches([]); return }
    let live = true
    const t = setTimeout(() => {
      void api.get<{ patients: PatientMatch[] }>(`/api/patients/search?q=${encodeURIComponent(term)}`)
        .then((r) => { if (live) setMatches(r.patients) })
        .catch(() => { /* search is best-effort */ })
    }, 180)
    return () => { live = false; clearTimeout(t) }
  }, [q, dialog.kind])

  /* The fee the summary shows is the server's quote, follow-up rate and all. */
  useEffect(() => {
    if (dialog.kind !== 'book' || !picked) { setQuote(null); return }
    let live = true
    void api.get<{ amount: string }>(`/api/appointments/quote?patientId=${picked.id}&doctorId=${doctorId}`)
      .then((r) => { if (live) { setQuote(r.amount); setError(null) } })
      .catch((err) => { if (live) setError(err instanceof ApiFailure ? err.message : 'Could not price this visit.') })
    return () => { live = false }
  }, [picked, doctorId, dialog.kind])

  async function submit() {
    if (busy) return
    if (override && !overrideReason.trim()) {
      setError('Give a reason for the override.')
      return
    }
    setBusy(true)
    setError(null)
    const overrideFields = override ? { override: true, overrideReason: overrideReason.trim() } : {}
    try {
      if (dialog.kind === 'book') {
        if (!picked) return
        const r = await api.post<{ warning: string | null }>('/api/appointments', {
          patientId: picked.id, doctorId, scheduledAt: dialog.slotIso, channel: 'phone',
          idempotencyKey: attemptKey.current, ...overrideFields,
        })
        onDone(r.warning ?? `Booked ${picked.fullName} at ${dialog.time}.`)
      } else if (dialog.kind === 'move') {
        if (!reason.trim()) return
        const r = await api.post<{ warning: string | null }>(`/api/appointments/${dialog.appt.id}/reschedule`, {
          scheduledAt: dialog.slotIso, reason: reason.trim(), ...overrideFields,
        })
        onDone(r.warning ?? `Moved ${dialog.appt.patientName} to ${dialog.time}.`)
      } else {
        if (!reason.trim()) return
        await api.post(`/api/appointments/${dialog.appt.id}/cancel`, { reason: reason.trim() })
        onDone()
      }
    } catch (err) {
      const text = err instanceof ApiFailure ? err.message : 'That did not work.'
      // The slot is gone: nothing in this dialog can fix that, so close it
      // and let the refreshed grid show the day as it now is.
      if (err instanceof ApiFailure && (err.code === 'SLOT_TAKEN' || err.code === 'SLOT_HELD')) {
        onError(text)
        onDone()
        return
      }
      setError(text)
      if (err instanceof ApiFailure && err.details.overridable === true) setOverridable(true)
    } finally {
      setBusy(false)
    }
  }

  const title = dialog.kind === 'book'
    ? `Book ${dialog.time}`
    : dialog.kind === 'move'
      ? `Move ${dialog.appt.patientName} to ${dialog.time}`
      : `Cancel ${dialog.appt.patientName}`

  const canSubmit = !busy
    && (dialog.kind === 'book' ? !!picked && quote !== null : !!reason.trim())
    && (!override || !!overrideReason.trim())

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal" role="dialog" aria-modal="true" aria-labelledby="appt-dialog-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onClose() }}
      >
        <h3 id="appt-dialog-title">{title}</h3>

        {dialog.kind === 'book' && (
          picked ? (
            <div className="picked-patient">
              <span><strong>{picked.fullName}</strong> · {picked.patientCode}{picked.mobile ? ` · ${picked.mobile}` : ''}</span>
              <button className="link" disabled={busy} onClick={() => { setPicked(null); setError(null) }}>Change</button>
            </div>
          ) : (
            <>
              <input autoFocus placeholder="Search patient by name, mobile or code" aria-label="Search patient"
                value={q} onChange={(e) => setQ(e.target.value)} />
              <div className="modal-matches">
                {matches.map((p) => (
                  <button key={p.id} type="button" className="ac-item" onClick={() => setPicked(p)}>
                    <div>{p.fullName} <span className="generic">{ageLabel(p)}/{genderShort(p.gender)}</span></div>
                    <div className="generic">{p.patientCode}{p.mobile ? ` · ${p.mobile}` : ''} · {p.visitCount} visits</div>
                  </button>
                ))}
                {q.trim().length >= 2 && matches.length === 0 && (
                  <div className="ac-empty">No match. Register the patient at reception first.</div>
                )}
                {q.trim().length < 2 && <div className="ac-empty">Select a patient to book.</div>}
              </div>
            </>
          )
        )}

        {/* The summary: what will be written, before it is. */}
        {dialog.kind !== 'cancel' && (dialog.kind === 'move' || picked) && (
          <dl className="booking-summary">
            <dt>Doctor</dt><dd>{doctorName}</dd>
            <dt>When</dt><dd>{prettyDate(date)} at {dialog.time}{dialog.past ? ' (already past)' : ''}</dd>
            {dialog.kind === 'book' && (
              <><dt>Fee</dt><dd>{quote === null ? '…' : `₹${Number(quote).toLocaleString('en-IN')}`} · collected at the desk</dd></>
            )}
          </dl>
        )}

        {(dialog.kind === 'move' || dialog.kind === 'cancel') && (
          <div className="field">
            <label htmlFor="reason">Reason</label>
            <input id="reason" autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder={dialog.kind === 'cancel' ? 'Why is it cancelled?' : 'Why the move?'} />
          </div>
        )}

        {error && <div className="msg error" role="alert">{error}</div>}

        {dialog.kind !== 'cancel' && overridable && (
          <div className="field">
            <label className="check">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
              {' '}Book anyway (override)
            </label>
            {override && (
              <input type="text" aria-label="Reason for the override" autoFocus maxLength={300}
                value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Why is this booked outside the schedule? Recorded with your name." />
            )}
          </div>
        )}

        <div className="actions">
          <button disabled={!canSubmit} aria-busy={busy} onClick={() => void submit()}>
            {busy
              ? (dialog.kind === 'cancel' ? 'Cancelling…' : 'Saving…')
              : dialog.kind === 'cancel' ? 'Cancel appointment' : dialog.kind === 'move' ? 'Confirm move' : 'Confirm booking'}
          </button>
          <button className="secondary" disabled={busy} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
